/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Button,
  DialogContentText,
  TextField,
  Grid,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { isEqual } from 'lodash';
import React from 'react';
import { CustomDialog, CustomIconButton } from '@opengeoweb/shared';
import type { serviceTypes } from '@opengeoweb/store';
import { Add } from '@opengeoweb/theme';
import { LayerType, queryWMSLayersTree } from '@opengeoweb/webmap';
import WMSLayerTreeConnect from './WMSLayerTree/WMSLayerTreeConnect';
import WMSServerList from './WMSServerList/WMSServerList';
import { preloadedDefaultMapServices } from '../../../utils/defaultConfigurations';
import { useCoreTranslation } from '../../../utils/i18n';

interface AddLayerPopupProps {
  onRenderTree?: (service: serviceTypes.InitialService) => React.ReactNode;
  preloadedServices?: serviceTypes.InitialService[];
  open?: boolean;
  handleClose?: () => void;
  layerType?: LayerType;
}

const validateServiceUrl = (url: string): boolean => {
  if (url === '' || url === null || typeof url === 'undefined') {
    return false;
  }
  const matcher = /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|[0-9a-zA-Z]+[:?\d]*)\S*$/;
  if (!matcher.test(url)) {
    return false;
  }
  return true;
};

const AddLayersPopup: React.FC<AddLayerPopupProps> = ({
  onRenderTree,
  preloadedServices = preloadedDefaultMapServices,
  open = false,
  handleClose,
  layerType = LayerType.mapLayer,
}: AddLayerPopupProps) => {
  const { t } = useCoreTranslation();
  const [serviceURL, setServiceURL] = React.useState('');
  const [services, setServices] = React.useState(preloadedServices!);
  const [activeService, setActiveService] = React.useState(
    preloadedServices![0],
  );
  const [urlError, setUrlError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleChangeService = (service: serviceTypes.InitialService): void => {
    setActiveService(service);
  };

  const handleAddToList = (newServiceName: string): void => {
    setServices([
      {
        name: newServiceName,
        url: serviceURL,
        id: serviceURL,
      },
      ...services,
    ]);
    setServiceURL('');
  };

  const getServiceIndex = (url: string): number =>
    services.findIndex((serviceInList) => serviceInList.url === url);

  const handleAddServiceURL = (): void => {
    setLoading(true);
    const serviceIndex = getServiceIndex(serviceURL);
    // Check if service already exists in the list
    if (serviceIndex !== -1) {
      setActiveService(services[serviceIndex]);
      setServiceURL('');
      setUrlError('');
      setLoading(false);
      // Check if valid URL was passed
    } else if (validateServiceUrl(serviceURL)) {
      // Check if service is WMS server
      queryWMSLayersTree(serviceURL)
        .then((layerTreeFromPromise) => {
          // Shorten URL as a naming backup
          const shortenedURL = serviceURL.substring(
            serviceURL.indexOf('//') + 2,
            serviceURL.indexOf('?'),
          );
          handleAddToList(
            layerTreeFromPromise.title ||
              layerTreeFromPromise.name ||
              shortenedURL,
          );
          setUrlError('');
          setLoading(false);
        })
        .catch(() => {
          setUrlError(t('wms-loader-error-valid-service'));
          setLoading(false);
        });
    } else {
      setUrlError(t('wms-loader-error-valid-url'));
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!isEqual(preloadedServices, services)) {
      setServices(preloadedServices!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadedServices]);

  return (
    <CustomDialog
      actions={
        <Button
          onClick={handleClose}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
          variant="tertiary"
        >
          {t('actions-close')}
        </Button>
      }
      aria-labelledby="form-dialog-title"
      data-testid="addLayersPopup"
      fullWidth
      maxWidth="md"
      onClose={() => handleClose?.()}
      open={open}
      title={t('add-layer-popup-title')}
    >
      <>
        <DialogContentText>{t('wms-loader-description')}</DialogContentText>

        <Grid alignItems="center" container direction="row" mb={1}>
          <Grid size={12}>
            <TextField
              disabled={loading}
              error={urlError !== ''}
              fullWidth
              helperText={urlError}
              id="serviceurl"
              label={t('add-layer-popup-service-url')}
              margin="dense"
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                const url = event.target.value.trim();
                setServiceURL(url);
                // No error message until new check
                setUrlError('');
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  handleAddServiceURL();
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {loading ? (
                        <CircularProgress
                          size={24}
                          sx={{
                            zIndex: 1,
                          }}
                        />
                      ) : (
                        <CustomIconButton
                          data-testid="add-service"
                          disabled={loading}
                          onClick={handleAddServiceURL}
                          size="large"
                        >
                          <Add />
                        </CustomIconButton>
                      )}
                    </InputAdornment>
                  ),
                },
              }}
              type="url"
              value={serviceURL}
              variant="filled"
            />
          </Grid>
        </Grid>
        <Grid alignItems="center" container direction="row" spacing={3}>
          <Grid size={4}>
            <WMSServerList
              availableServices={services!}
              handleChangeService={handleChangeService}
              service={activeService}
            />
          </Grid>
          <Grid size={8}>
            {onRenderTree ? (
              onRenderTree(activeService)
            ) : (
              <WMSLayerTreeConnect
                layerType={layerType}
                mapId=""
                service={activeService}
              />
            )}
          </Grid>
        </Grid>
      </>
    </CustomDialog>
  );
};

export default AddLayersPopup;
